/**
 * Types for Knowledge Base Service
 */

export interface KnowledgeBaseDocument {
  id: string
  tenant_id: string
  title: string
  content: string
  content_type: string
  source_url?: string
  source_type?: string
  metadata: Record<string, unknown>
  tags: string[]
  category?: string
  is_active: boolean
  injection_mode: 'direct' | 'rag' | 'auto'
  priority: number
  embedding_status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped'
  embedding_error?: string
  token_count?: number
  last_embedded_at?: string
  created_at: string
  updated_at: string
}

export interface SearchResult {
  /** Embedding ID */
  id: string
  /** Document ID */
  documentId: string
  /** Chunk index within document */
  chunkIndex: number
  /** Matched text chunk */
  chunkText: string
  /** Chunk metadata */
  chunkMetadata: Record<string, unknown>
  /** Similarity score (0-1, higher is better) */
  similarityScore: number
  /** Document title */
  documentTitle: string
  /** Document category */
  documentCategory?: string
  /** Document tags */
  documentTags: string[]
  /** From a document the agent learned from its conversations, which the prompt puts after the business's own. */
  learned: boolean
}

export interface SearchOptions {
  /** Explicit entity tokens from the customer; combined with semantic retrieval. */
  exactTerms?: string[]
  /** Maximum number of results (default: 5) */
  limit?: number
  /** Minimum similarity threshold (default: 0.7) */
  threshold?: number
  /** Enable plain text fallback search when vector search returns nothing or errors */
  useFallback?: boolean
  /** Filter by category */
  category?: string
  /** Filter by tags (any match) */
  tags?: string[]
}

export interface KnowledgeContext {
  /** Direct injection documents (full content) */
  directDocs: Array<{
    title: string
    content: string
    priority: number
  }>
  /** RAG search results (chunks) */
  ragResults: SearchResult[]
  /** Formatted context string for prompt */
  formattedContext: string
}

export interface GetContextOptions {
  /** Bounded, individually resolved requests; undefined preserves single-query callers. */
  searches?: import('./retrieval-plan').RetrievalSearch[]
  /** Invoked only when selected documents actually have a completed RAG index. */
  resolveSearches?: () => Promise<import('./retrieval-plan').RetrievalSearch[]>
  /** Include RAG search results (default: true) */
  includeRAG?: boolean
  /** RAG search limit (default: 5) */
  ragLimit?: number
  /** RAG similarity threshold (default: 0.7) */
  ragThreshold?: number
  /** Filter by category */
  category?: string
  /** Filter by tags */
  tags?: string[]
}

// ============================================================================
// File Parser Types
// ============================================================================

/** Supported file types for Knowledge Base upload */
export type SupportedFileType = 'pdf' | 'docx' | 'txt' | 'md' | 'xlsx' | 'xls' | 'csv'

/** MIME type to file type mapping */
export const SUPPORTED_MIME_TYPES: Record<string, SupportedFileType> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  // Spreadsheet types
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'text/csv': 'csv',
  'application/csv': 'csv',
} as const

/** File extension to file type mapping (fallback) */
export const EXTENSION_TO_FILE_TYPE: Record<string, SupportedFileType> = {
  pdf: 'pdf',
  docx: 'docx',
  txt: 'txt',
  md: 'md',
  // Spreadsheet types
  xlsx: 'xlsx',
  xls: 'xls',
  csv: 'csv',
} as const

/** File parsing result */
export interface FileParseResult {
  /** Extracted text content */
  text: string
  /** File metadata */
  metadata: FileParseMetadata
}

/** Metadata extracted during file parsing */
export interface FileParseMetadata {
  /** Number of pages (PDF only) */
  pageCount?: number
  /** Word count */
  wordCount: number
  /** Character count */
  charCount: number
  /** Detected file type */
  fileType: SupportedFileType
}

/** File upload metadata stored in document */
export interface FileUploadMetadata {
  /** Original filename */
  original_filename: string
  /** Original file size in bytes */
  original_size: number
  /** Original MIME type */
  original_type: string
  /** Path in storage (if stored) */
  file_path?: string
  /** Page count (PDF only) */
  page_count?: number
  /** Word count */
  word_count: number
  /** Character count */
  char_count: number
}

/** Constants for file upload */
export const FILE_UPLOAD_CONSTANTS = {
  /** Maximum file size in bytes (10MB) */
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  /** Supabase storage bucket name */
  STORAGE_BUCKET: 'kb-files',
  /** Supported file extensions */
  SUPPORTED_EXTENSIONS: ['pdf', 'docx', 'txt', 'md', 'xlsx', 'xls', 'csv'] as const,
} as const

/** Check if file type is a spreadsheet */
export function isSpreadsheetType(fileType: SupportedFileType): boolean {
  return fileType === 'xlsx' || fileType === 'xls' || fileType === 'csv'
}
